import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Modal, Button, AlertMessage } from "components/atoms";
import {
  TextInput,
  EmailInput,
  GeneralSelectInput,
  PhoneNumberInput,
} from "components/molecules";
import { useContentSanitizer, useUserData } from "hooks";
import { inquiryService, sanitizeContent, sanitizePhoneNumber } from "services";
import { countriesFlat, countryCodes } from "types";
import "./InquiryModal.scss";

interface InquiryModalProps {
  isOpen: boolean;
  onClose: () => void;
  courseData?: {
    courseId?: string | null;
    courseName?: string | null;
    institutionId: string | null;
    institution?: string | null;
    country?: string | null;
  };
}

const inquirySchema = Yup.object().shape({
  email: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  phone: Yup.object({
    countryCode: Yup.string(),
    areaCode: Yup.string().nullable(),
    number: Yup.string(),
    extension: Yup.string().nullable(),
  }).test("phone-required", "Phone number is required", function (value) {
    const hasPhone = value && value.number && value.number.trim();

    return !!hasPhone;
  }),
  country: Yup.string().required("Country is required"),
});

const InquiryModal: React.FC<InquiryModalProps> = ({
  isOpen,
  onClose,
  courseData,
}) => {
  const { withSanitization } = useContentSanitizer();
  const { userData } = useUserData();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  if (!courseData?.courseId || !courseData?.institutionId) {
    onClose();
  }

  const inquiryFormik = useFormik({
    initialValues: {
      email: userData?.email || "",
      firstName: userData?.personalInformation?.basicDetails?.firstName || "",
      lastName: userData?.personalInformation?.basicDetails?.lastName || "",
      phone: {
        countryCode:
          userData?.personalInformation?.contactDetails.phone?.countryCode ||
          "",
        areaCode:
          userData?.personalInformation?.contactDetails.phone?.areaCode || "",
        number:
          userData?.personalInformation?.contactDetails.phone?.number || "",
        extension:
          userData?.personalInformation?.contactDetails.phone?.extension || "",
      },
      courseId: courseData?.courseId || "",
      courseName: courseData?.courseName || "",
      institutionId: courseData?.institutionId || "",
      institution: courseData?.institution || "",
      country: courseData?.country || "",
    },
    validationSchema: inquirySchema,
    enableReinitialize: true,
    onSubmit: withSanitization(async (values) => {
      try {
        const variables = {
          email: sanitizeContent(values.email),
          firstName: sanitizeContent(values.firstName),
          lastName: sanitizeContent(values.lastName),
          phone: {
            countryCode: sanitizeContent(values.phone.countryCode),
            areaCode: sanitizeContent(values.phone.areaCode || ""),
            number: sanitizePhoneNumber(sanitizeContent(values.phone.number)),
            extension: sanitizeContent(values.phone.extension || ""),
          },
          courseId: sanitizeContent(courseData?.courseId || ""),
          courseName: sanitizeContent(courseData?.courseName || ""),
          institutionId: sanitizeContent(courseData?.institutionId || ""),
          institution: sanitizeContent(courseData?.institution || ""),
          country: sanitizeContent(values.country),
          source: "website",
        };

        await inquiryService.createInquiry(variables);

        setIsSubmitted(true);
        setErrorMessage(null);
      } catch (error) {
        setErrorMessage("Failed to submit form. Please try again.");
      }
    }),
  });

  const handleSubmit = () => {
    inquiryFormik.handleSubmit();
  };

  const renderInquirySection = () => (
    <>
      <div className="section-header">
        <h4>
          Interested in: <strong>{courseData?.courseName}</strong>
        </h4>
      </div>

      <div className="form-fields">
        <EmailInput
          id="email"
          label="Email *"
          getFieldProps={() => ({
            value: inquiryFormik.values.email,
            onChange: inquiryFormik.handleChange,
            onBlur: inquiryFormik.handleBlur,
          })}
          touched={inquiryFormik.touched.email}
          error={inquiryFormik.errors.email}
        />
      </div>
      <div className="form-fields">
        <TextInput
          id="firstName"
          label="First Name  *"
          getFieldProps={() => ({
            value: inquiryFormik.values.firstName,
            onChange: inquiryFormik.handleChange,
            onBlur: inquiryFormik.handleBlur,
          })}
          touched={inquiryFormik.touched.firstName}
          error={inquiryFormik.errors.firstName}
        />
      </div>
      <div className="form-fields">
        <TextInput
          id="lastName"
          label="Last Name *"
          getFieldProps={() => ({
            value: inquiryFormik.values.lastName,
            onChange: inquiryFormik.handleChange,
            onBlur: inquiryFormik.handleBlur,
          })}
          touched={inquiryFormik.touched.lastName}
          error={inquiryFormik.errors.lastName}
        />
      </div>
      <div className="form-fields">
        <PhoneNumberInput
          id="phone"
          label="Phone Number *"
          value={inquiryFormik.values.phone}
          onChange={(value) => inquiryFormik.setFieldValue("phone", value)}
          onBlur={() => {
            inquiryFormik.setFieldTouched("phone.countryCode", true);
            inquiryFormik.setFieldTouched("phone.areaCode", true);
            inquiryFormik.setFieldTouched("phone.number", true);
            inquiryFormik.setFieldTouched("phone.extension", true);
          }}
          countryCodes={countryCodes}
          error={
            typeof inquiryFormik.errors.phone === "string"
              ? inquiryFormik.errors.phone
              : undefined
          }
          touched={{
            countryCode: inquiryFormik.touched.phone?.countryCode,
            areaCode: inquiryFormik.touched.phone?.areaCode,
            number: inquiryFormik.touched.phone?.number,
            extension: inquiryFormik.touched.phone?.extension,
          }}
          showExtension={false}
        />
      </div>
      <div className="form-fields">
        <TextInput
          id="courseName"
          label="Course of Interest *"
          value={inquiryFormik.values.courseName}
          getFieldProps={() => ({
            value: inquiryFormik.values.courseName,
            onChange: inquiryFormik.handleChange,
            onBlur: inquiryFormik.handleBlur,
          })}
          touched={inquiryFormik.touched.courseName}
          error={inquiryFormik.errors.courseName}
          disabled={!!courseData?.courseName}
        />
      </div>
      <div className="form-fields">
        <GeneralSelectInput
          id="country"
          label="Country of Interest *"
          options={countriesFlat}
          onChange={inquiryFormik.handleChange}
          onBlur={inquiryFormik.handleBlur}
          value={inquiryFormik.values.country}
          touched={inquiryFormik.touched.country}
          error={inquiryFormik.errors.country}
        />
      </div>
      {errorMessage && <AlertMessage type="error">{errorMessage}</AlertMessage>}
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Enquiry Form`}
      className="inquiry-modal"
    >
      <div className="inquiry-section">
        <div className="form-section">
          <div className="section-header">
            {isSubmitted ? (
              <h4>
                We've received your information and will be in touch soon with
                personalized course recommendations
              </h4>
            ) : (
              renderInquirySection()
            )}
          </div>

          {isSubmitted ? (
            <Button
              type="button"
              text="Close"
              onClick={onClose}
              className="form-button"
              width="6rem"
              variant="primary"
            />
          ) : (
            <Button
              type="submit"
              text="Submit"
              onClick={handleSubmit}
              className="inquiry-button"
              width="6rem"
              variant="primary"
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default InquiryModal;
